import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** 是否显示选中勾选标记，默认 true */
  checkmark?: boolean;
  className?: string;
  /** sm 适合表格内；md 为表单默认 */
  size?: 'sm' | 'md';
  /** 下拉面板最小宽度，默认与触发器同宽 */
  panelClassName?: string;
}

/**
 * 自定义下拉选择（单选），样式参考 PrimeVue Select / Listbox
 * https://primevue.dev/listbox/
 */
export default function Select({
  options,
  value,
  onChange,
  placeholder = '请选择',
  disabled = false,
  invalid = false,
  checkmark = true,
  className = '',
  size = 'md',
  panelClassName = '',
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value);
  const enabledOptions = options.filter((o) => !o.disabled);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) {
      setHighlightIndex(-1);
      return;
    }
    const idx = enabledOptions.findIndex((o) => o.value === value);
    setHighlightIndex(idx >= 0 ? idx : 0);
  }, [open, value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || highlightIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-index="${highlightIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open]);

  const selectAt = (index: number) => {
    const opt = enabledOptions[index];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (disabled) return;

    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(enabledOptions.length - 1, Math.max(0, i) + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(0, (i < 0 ? 0 : i) - 1));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setHighlightIndex(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setHighlightIndex(Math.max(0, enabledOptions.length - 1));
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (highlightIndex >= 0) selectAt(highlightIndex);
    }
  };

  const pad = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm';
  const itemPad = size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          if (!disabled) setOpen((v) => !v);
        }}
        onKeyDown={onKeyDown}
        className={`w-full flex items-center gap-2 rounded-lg border bg-white outline-none transition-colors ${pad} ${
          disabled
            ? 'opacity-50 cursor-not-allowed bg-background-100 border-background-200'
            : open
              ? 'border-primary-400 ring-2 ring-primary-100'
              : invalid
                ? 'border-primary-400 hover:border-primary-500'
                : 'border-background-300 hover:border-primary-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100'
        }`}
      >
        <span
          className={`flex-1 min-w-0 text-left truncate ${
            selected ? 'text-foreground-800' : 'text-foreground-400'
          }`}
        >
          {selected?.label || placeholder}
        </span>
        <i
          className={`ri-arrow-down-s-line text-foreground-400 shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && !disabled && (
        <div
          className={`absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-background-200 bg-white shadow-lg overflow-hidden ${panelClassName}`}
        >
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-activedescendant={
              highlightIndex >= 0 ? `${listId}-opt-${highlightIndex}` : undefined
            }
            className="max-h-56 overflow-y-auto py-1"
          >
            {options.length === 0 ? (
              <li className={`${itemPad} text-foreground-400 text-center`}>暂无选项</li>
            ) : (
              options.map((opt) => {
                const enabledIdx = enabledOptions.findIndex((o) => o.value === opt.value);
                const isSelected = opt.value === value;
                const isHighlight = !opt.disabled && enabledIdx === highlightIndex;

                return (
                  <li
                    key={opt.value || `__empty_${opt.label}`}
                    id={enabledIdx >= 0 ? `${listId}-opt-${enabledIdx}` : undefined}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    data-index={enabledIdx >= 0 ? enabledIdx : undefined}
                    onMouseEnter={() => {
                      if (!opt.disabled && enabledIdx >= 0) setHighlightIndex(enabledIdx);
                    }}
                    onClick={() => {
                      if (opt.disabled || enabledIdx < 0) return;
                      selectAt(enabledIdx);
                    }}
                    className={`${itemPad} flex items-center gap-2 cursor-pointer transition-colors ${
                      opt.disabled
                        ? 'opacity-40 cursor-not-allowed text-foreground-400'
                        : isSelected
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : isHighlight
                            ? 'bg-background-100 text-foreground-800'
                            : 'text-foreground-700 hover:bg-background-50'
                    }`}
                  >
                    {checkmark && (
                      <i
                        className={`ri-check-line text-sm shrink-0 ${
                          isSelected ? 'text-primary-600 opacity-100' : 'opacity-0'
                        }`}
                      />
                    )}
                    <span className="truncate flex-1">{opt.label}</span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
